// team/shared/features/card/TeamQRCodeModal.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Button, CircularProgress, IconButton } from '@mui/material';
import { DownloadIcon, CloseIcon } from 'icons/materialIcons';
import TeamModal from 'team/shared/components/TeamModal';
import QRCodeStyling from 'qr-code-styling';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';

const TeamQRCodeModal = ({ open, onClose, cardId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);

    const qrRef = useRef(null);
    const containerRef = useRef(null);
    const downloadQrRef = useRef(null);

    const BASE_URL = `${FRONTEND_BASE_URL}/card`;

    // Use useMemo to stabilize the URL and prevent constant regeneration
    const cardUrl = useMemo(() => {
        if (!cardId) return '';
        return `${BASE_URL}/${cardId}?source=qr_code`;
    }, [cardId, BASE_URL]);

    // Create display QR code - optimized for instant display
    useEffect(() => {
        console.log('QR Modal Effect - open:', open, 'cardId:', cardId, 'cardUrl:', cardUrl);

        if (!open || !cardId || !cardUrl) {
            console.log('QR Modal Effect - Missing required data');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Create QR code instance
            const qrCode = new QRCodeStyling({
                width: 300,
                height: 300,
                type: 'svg',
                data: cardUrl,
                image: '/logo192.png',
                dotsOptions: {
                    color: '#000000',
                    type: 'rounded'
                },
                backgroundOptions: {
                    color: '#ffffff',
                },
                imageOptions: {
                    crossOrigin: 'anonymous',
                    margin: 10
                },
                cornersSquareOptions: {
                    color: '#000000',
                    type: 'extra-rounded'
                },
                cornersDotOptions: {
                    color: '#000000',
                    type: 'dot'
                }
            });

            // Clear previous QR code
            if (qrRef.current) {
                qrRef.current.innerHTML = '';
            }

            // Append QR code to container
            qrCode.append(qrRef.current);
            setLoading(false);

            console.log('QR Code created successfully for URL:', cardUrl);

        } catch (err) {
            console.error('Error creating QR code:', err);
            setError('Failed to generate QR code');
            setLoading(false);
        }
    }, [open, cardId, cardUrl]);

    // Create download QR code - higher quality for download
    useEffect(() => {
        if (!open || !cardId || !cardUrl) return;

        try {
            const downloadQrCode = new QRCodeStyling({
                width: 600,
                height: 600,
                type: 'canvas',
                data: cardUrl,
                image: '/logo192.png',
                dotsOptions: {
                    color: '#000000',
                    type: 'rounded'
                },
                backgroundOptions: {
                    color: '#ffffff',
                },
                imageOptions: {
                    crossOrigin: 'anonymous',
                    margin: 20
                },
                cornersSquareOptions: {
                    color: '#000000',
                    type: 'extra-rounded'
                },
                cornersDotOptions: {
                    color: '#000000',
                    type: 'dot'
                }
            });

            if (downloadQrRef.current) {
                downloadQrRef.current.innerHTML = '';
                downloadQrCode.append(downloadQrRef.current);
            }

        } catch (err) {
            console.error('Error creating download QR code:', err);
        }
    }, [open, cardId, cardUrl]);

    const handleDownload = async () => {
        if (!downloadQrRef.current) return;

        setDownloading(true);
        try {
            const canvas = downloadQrRef.current.querySelector('canvas');
            if (canvas) {
                const link = document.createElement('a');
                link.download = `qr-code-${cardId}.png`;
                link.href = canvas.toDataURL();
                link.click();
            }
        } catch (err) {
            console.error('Error downloading QR code:', err);
            setError('Failed to download QR code');
        } finally {
            setDownloading(false);
        }
    };

    const handleClose = () => {
        setError('');
        setLoading(false);
        setDownloading(false);
        onClose();
    };

    return (
        <TeamModal open={open} onClose={handleClose}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        QR Code
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Scan this QR code to view the card
                    </Typography>

                    <Box
                        ref={containerRef}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '300px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            p: 2,
                            backgroundColor: '#fafafa'
                        }}
                    >
                        {loading && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <CircularProgress size={40} />
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Generating QR code...
                                </Typography>
                            </Box>
                        )}

                        {error && (
                            <Typography color="error" variant="body2">
                                {error}
                            </Typography>
                        )}

                        <Box
                            ref={qrRef}
                            sx={{
                                display: loading || error ? 'none' : 'block',
                                '& svg': {
                                    maxWidth: '100%',
                                    height: 'auto'
                                }
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        disabled={loading || downloading || !!error}
                        sx={{ minWidth: 120 }}
                    >
                        {downloading ? 'Downloading...' : 'Download'}
                    </Button>
                </Box>

                {/* Hidden download QR code */}
                <Box sx={{ display: 'none' }}>
                    <Box ref={downloadQrRef} />
                </Box>
            </Box>
        </TeamModal>
    );
};

export default TeamQRCodeModal;